/**
 * The 4 framework pages per M3X.REPORTS PR 14-18:
 *   - SavedReportsPage      (/reports/saved)
 *   - ScheduledExportsPage  (/reports/scheduled)
 *   - ExportsLogPage        (/reports/exports — replaces ComingSoon)
 *   - CustomBuilderPage     (/reports/custom-builder)
 *
 * Each is a thin React surface on top of the api/routes/reports_library
 * backend. v1 surfaces the full CRUD; the cron-worker that fires
 * scheduled exports automatically lands when Railway scheduled
 * jobs are configured (manual `Run now` button works today).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Download,
  Pin,
  PinOff,
  Play,
  Share2,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../../components/ui/EmptyState";
import { PageHeader } from "../../components/ui/PageHeader";
import { apiFetch } from "../../lib/api";
import { reportById, REPORTS_CATALOG } from "../../lib/reports/catalog";

type SavedReport = {
  id: string;
  report_id: string;
  name: string;
  description?: string;
  is_shared: boolean;
  is_mc_pinned: boolean;
  user_id: string;
  created_at: string;
  updated_at: string;
};

type ScheduledExport = {
  id: string;
  report_id: string;
  name: string;
  format: string;
  cadence_cron: string;
  status: string;
  retention_days: number;
  created_by: string;
  created_at: string;
  last_run_at?: string;
  next_run_at?: string;
  last_error?: string;
};

type ExportArchive = {
  id: string;
  scheduled_export_id?: string;
  report_id: string;
  format: string;
  generated_at: string;
  generated_by: string;
  filename: string;
  size_bytes?: number;
  status: string;
  expires_at?: string;
};

const _USER_PLACEHOLDER = "anonymous"; // v1: Clerk wiring lands in follow-up

// ---------------------------------------------------------------------------
// /reports/saved
// ---------------------------------------------------------------------------

export function SavedReportsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["saved-reports"],
    queryFn: async (): Promise<SavedReport[]> => {
      const res = await apiFetch(
        `/api/reports/saved-reports?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
      );
      if (!res.ok) throw new Error(`saved-reports ${res.status}`);
      return res.json();
    },
  });
  const mut = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<SavedReport> }) => {
      const existing = q.data?.find((r) => r.id === id);
      const body = { ...existing, ...patch };
      const res = await apiFetch(
        `/api/reports/saved-reports/${id}?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) throw new Error(`update ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-reports"] }),
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      await apiFetch(
        `/api/reports/saved-reports/${id}?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-reports"] }),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("reports.saved_title", "Saved reports")}
        subtitle={t(
          "reports.saved_subtitle",
          "Your starred reports — saved view state, pinnable to Mission Control, shareable with your team.",
        )}
        breadcrumbs={[
          { label: t("reports.breadcrumb_root", "Reports"), href: "/reports" },
          { label: t("reports.saved_title", "Saved reports") },
        ]}
      />
      {q.isLoading && <p className="text-sm text-text-tertiary">Loading…</p>}
      {q.data && q.data.length === 0 && (
        <EmptyState
          icon={Star}
          headline={t("reports.saved_empty_title", "No saved reports yet")}
          description={t(
            "reports.saved_empty_desc",
            "Open any report and click the star to save it here.",
          )}
        />
      )}
      {q.data && q.data.length > 0 && (
        <ul className="flex flex-col divide-y divide-border-subtle rounded-xl border border-border-subtle bg-surface-default">
          {q.data.map((r) => (
            <li key={r.id} className="flex items-center gap-3 p-3">
              <Star size={14} className="text-amber-500" aria-hidden />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-text-primary">{r.name}</h3>
                {r.description && (
                  <p className="mt-0.5 text-xs text-text-tertiary">{r.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  mut.mutate({ id: r.id, patch: { is_mc_pinned: !r.is_mc_pinned } })
                }
                className="rounded p-1.5 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
                aria-label={
                  r.is_mc_pinned
                    ? t("reports.unpin_mc", "Unpin from Mission Control")
                    : t("reports.pin_mc", "Pin to Mission Control")
                }
                title={
                  r.is_mc_pinned
                    ? t("reports.unpin_mc", "Unpin from Mission Control")
                    : t("reports.pin_mc", "Pin to Mission Control")
                }
              >
                {r.is_mc_pinned ? <PinOff size={14} /> : <Pin size={14} />}
              </button>
              <button
                type="button"
                onClick={() =>
                  mut.mutate({ id: r.id, patch: { is_shared: !r.is_shared } })
                }
                className="rounded p-1.5 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
                aria-label={
                  r.is_shared
                    ? t("reports.unshare", "Unshare")
                    : t("reports.share", "Share with team")
                }
                title={
                  r.is_shared
                    ? t("reports.unshare", "Unshare")
                    : t("reports.share", "Share with team")
                }
              >
                <Share2 size={14} aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => del.mutate(r.id)}
                className="rounded p-1.5 text-text-tertiary hover:bg-rose-50 hover:text-rose-600"
                aria-label={t("reports.delete", "Delete")}
                title={t("reports.delete", "Delete")}
              >
                <Trash2 size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// /reports/scheduled
// ---------------------------------------------------------------------------

export function ScheduledExportsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [reportId, setReportId] = useState(REPORTS_CATALOG[0]?.id ?? "");
  const [format, setFormat] = useState<"xlsx" | "csv" | "pdf">("xlsx");
  const [cadence, setCadence] = useState("0 7 * * 1"); // Monday 7am

  const q = useQuery({
    queryKey: ["scheduled-exports"],
    queryFn: async (): Promise<ScheduledExport[]> => {
      const res = await apiFetch(`/api/reports/scheduled-exports`);
      if (!res.ok) throw new Error(`scheduled ${res.status}`);
      return res.json();
    },
  });
  const create = useMutation({
    mutationFn: async () => {
      const body = {
        report_id: reportId,
        name,
        format,
        cadence_cron: cadence,
        retention_days: 30,
      };
      const res = await apiFetch(
        `/api/reports/scheduled-exports?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) throw new Error(`create ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-exports"] });
      setCreating(false);
      setName("");
    },
  });
  const runNow = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(
        `/api/reports/scheduled-exports/${id}/run-now?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(`run-now ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-exports"] });
      qc.invalidateQueries({ queryKey: ["exports-archive"] });
    },
  });
  const setStatus = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "pause" | "resume" }) => {
      const res = await apiFetch(
        `/api/reports/scheduled-exports/${id}/${action}`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(`${action} ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduled-exports"] }),
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      await apiFetch(`/api/reports/scheduled-exports/${id}`, { method: "DELETE" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduled-exports"] }),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("reports.scheduled_title", "Scheduled exports")}
        subtitle={t(
          "reports.scheduled_subtitle",
          "Cron-style scheduled exports — daily / weekly / monthly. Generated files land in the Exports Log with the chosen retention window.",
        )}
        breadcrumbs={[
          { label: t("reports.breadcrumb_root", "Reports"), href: "/reports" },
          { label: t("reports.scheduled_title", "Scheduled exports") },
        ]}
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setCreating(!creating)}
          className="rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep"
        >
          {creating
            ? t("reports.scheduled_cancel", "Cancel")
            : t("reports.scheduled_new", "+ New schedule")}
        </button>
      </div>
      {creating && (
        <div className="rounded-xl border border-border-subtle bg-surface-sunken p-4">
          <h3 className="mb-3 text-sm font-medium text-text-primary">
            {t("reports.scheduled_new_title", "New scheduled export")}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("reports.scheduled_name_ph", "Schedule name")}
              className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
            />
            <select
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
            >
              {REPORTS_CATALOG.map((r) => (
                <option key={r.id} value={r.id}>
                  {t(r.titleKey, r.titleKey)}
                </option>
              ))}
            </select>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as "xlsx" | "csv" | "pdf")}
              className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
            >
              <option value="xlsx">XLSX</option>
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
            <input
              type="text"
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              placeholder="0 7 * * 1 (Mon 7am)"
              className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm font-mono"
            />
          </div>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!name.trim()}
            className="mt-3 rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep disabled:opacity-50"
          >
            {t("reports.scheduled_create", "Create schedule")}
          </button>
        </div>
      )}
      {q.isLoading && <p className="text-sm text-text-tertiary">Loading…</p>}
      {q.data && q.data.length === 0 && (
        <EmptyState
          icon={CalendarClock}
          headline={t("reports.scheduled_empty_title", "No scheduled exports yet")}
          description={t(
            "reports.scheduled_empty_desc",
            "Create your first schedule above. Cron-style cadence (e.g. `0 7 * * 1` for Monday 7am).",
          )}
        />
      )}
      {q.data && q.data.length > 0 && (
        <table className="w-full overflow-hidden rounded-xl border border-border-subtle bg-surface-default text-sm">
          <thead className="bg-surface-sunken text-left text-xs text-text-tertiary">
            <tr>
              <th className="px-3 py-2">{t("reports.col_name", "Name")}</th>
              <th className="px-3 py-2">{t("reports.col_report", "Report")}</th>
              <th className="px-3 py-2">{t("reports.col_format", "Format")}</th>
              <th className="px-3 py-2">{t("reports.col_cadence", "Cadence")}</th>
              <th className="px-3 py-2">{t("reports.col_status", "Status")}</th>
              <th className="px-3 py-2">{t("reports.col_last_run", "Last run")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((s) => {
              const m = reportById(s.report_id);
              return (
                <tr key={s.id} className="border-t border-border-subtle">
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-text-secondary">
                    {m ? t(m.titleKey, m.titleKey) : s.report_id}
                  </td>
                  <td className="px-3 py-2 uppercase">{s.format}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.cadence_cron}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${s.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-text-tertiary/10 text-text-tertiary"}`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-text-tertiary">
                    {s.last_run_at ? new Date(s.last_run_at).toISOString().slice(0, 16).replace("T", " ") : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => runNow.mutate(s.id)}
                        title={t("reports.run_now", "Run now")}
                        aria-label={t("reports.run_now", "Run now")}
                        className="rounded p-1.5 text-brand-blue hover:bg-brand-blue/10"
                      >
                        <Play size={14} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setStatus.mutate({
                            id: s.id,
                            action: s.status === "active" ? "pause" : "resume",
                          })
                        }
                        title={s.status === "active"
                          ? t("reports.pause", "Pause")
                          : t("reports.resume", "Resume")}
                        aria-label={s.status === "active"
                          ? t("reports.pause", "Pause")
                          : t("reports.resume", "Resume")}
                        className="rounded p-1.5 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
                      >
                        <X size={14} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => del.mutate(s.id)}
                        title={t("reports.delete", "Delete")}
                        aria-label={t("reports.delete", "Delete")}
                        className="rounded p-1.5 text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// /reports/exports — replaces ComingSoon
// ---------------------------------------------------------------------------

export function ExportsLogPage() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["exports-archive"],
    queryFn: async (): Promise<ExportArchive[]> => {
      const res = await apiFetch(`/api/reports/exports-archive?limit=200`);
      if (!res.ok) throw new Error(`archive ${res.status}`);
      return res.json();
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("reports.exports_log_title", "Exports log")}
        subtitle={t(
          "reports.exports_log_subtitle",
          "Every generated export from scheduled runs + manual exports. Kept for the schedule's retention window.",
        )}
        breadcrumbs={[
          { label: t("reports.breadcrumb_root", "Reports"), href: "/reports" },
          { label: t("reports.exports_log_title", "Exports log") },
        ]}
      />
      {q.isLoading && <p className="text-sm text-text-tertiary">Loading…</p>}
      {q.data && q.data.length === 0 && (
        <EmptyState
          icon={Download}
          headline={t("reports.exports_log_empty_title", "No exports yet")}
          description={t(
            "reports.exports_log_empty_desc",
            "Scheduled exports + manual exports both appear here as they're generated.",
          )}
        />
      )}
      {q.data && q.data.length > 0 && (
        <table className="w-full overflow-hidden rounded-xl border border-border-subtle bg-surface-default text-sm">
          <thead className="bg-surface-sunken text-left text-xs text-text-tertiary">
            <tr>
              <th className="px-3 py-2">{t("reports.col_filename", "Filename")}</th>
              <th className="px-3 py-2">{t("reports.col_report", "Report")}</th>
              <th className="px-3 py-2">{t("reports.col_format", "Format")}</th>
              <th className="px-3 py-2">{t("reports.col_generated", "Generated")}</th>
              <th className="px-3 py-2">{t("reports.col_status", "Status")}</th>
              <th className="px-3 py-2">{t("reports.col_expires", "Expires")}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((a) => {
              const m = reportById(a.report_id);
              return (
                <tr key={a.id} className="border-t border-border-subtle">
                  <td className="px-3 py-2 font-mono text-xs">{a.filename}</td>
                  <td className="px-3 py-2">{m ? t(m.titleKey, m.titleKey) : a.report_id}</td>
                  <td className="px-3 py-2 uppercase">{a.format}</td>
                  <td className="px-3 py-2 text-xs text-text-tertiary">
                    {new Date(a.generated_at).toISOString().slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${a.status === "success" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-text-tertiary">
                    {a.expires_at ? new Date(a.expires_at).toISOString().slice(0, 10) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// /reports/custom-builder
// ---------------------------------------------------------------------------

export function CustomBuilderPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [entity, setEntity] = useState("employees");

  const q = useQuery({
    queryKey: ["custom-reports"],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/reports/custom-reports?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
      );
      if (!res.ok) throw new Error(`custom ${res.status}`);
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(
        `/api/reports/custom-reports?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            entity,
            column_config: [],
            filter_config: [],
            is_shared: false,
          }),
        },
      );
      if (!res.ok) throw new Error(`create ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-reports"] });
      setName("");
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("reports.builder_title", "Custom report builder")}
        subtitle={t(
          "reports.builder_subtitle",
          "Pick an entity, configure columns and filters, save as a personal or shared view. v1: single-entity table; joins land in M3X.PLATFORM-MINIMAL.",
        )}
        breadcrumbs={[
          { label: t("reports.breadcrumb_root", "Reports"), href: "/reports" },
          { label: t("reports.builder_title", "Custom report builder") },
        ]}
      />
      <div className="rounded-xl border border-border-subtle bg-surface-sunken p-4">
        <h3 className="mb-3 text-sm font-medium text-text-primary">
          {t("reports.builder_new_title", "New custom report")}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("reports.builder_name_ph", "Report name")}
            className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
          />
          <select
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
          >
            <option value="employees">Employees</option>
            <option value="contracts">Contracts</option>
            <option value="leaves">Leaves</option>
            <option value="loans">Loans</option>
            <option value="payroll_runs">Payroll Runs</option>
            <option value="terminations">Terminations</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!name.trim()}
          className="mt-3 rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep disabled:opacity-50"
        >
          {t("reports.builder_save", "Save report")}
        </button>
      </div>
      {q.data && Array.isArray(q.data) && q.data.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-text-primary">
            {t("reports.builder_my_reports", "My custom reports")}
          </h2>
          <ul className="flex flex-col divide-y divide-border-subtle rounded-xl border border-border-subtle bg-surface-default">
            {q.data.map((r: { id: string; name: string; entity: string }) => (
              <li key={r.id} className="flex items-center gap-3 p-3 text-sm">
                <Star size={14} className="text-brand-blue" aria-hidden />
                <span className="font-medium">{r.name}</span>
                <span className="text-text-tertiary text-xs">→ {r.entity}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
